import RFB from "@novnc/novnc";
import { message } from "antd";

class ProxyDisplayManager {
  constructor(options = {}) {
    this.container = null;
    this.rfb = null;
    this.connected = false;
    this.url = options.url;
    this.credentials = options.credentials || { password: "" };
    this.onConnect = options.onConnect || (() => {});
    this.onDisconnect = options.onDisconnect || (() => {});
    this.onCredentialsRequired = options.onCredentialsRequired || (() => {});
    this.onDesktopName = options.onDesktopName || (() => {});
    this.viewOnly = options.viewOnly || false;
    this.scaleViewport = options.scaleViewport || true;
    this.clipboardText = "";
    this.audioEnabled = false;
    this.recordingEnabled = false;
    this.clipViewport = options.clipViewport || false;
    this.resizeSession = options.resizeSession || false;
    this.session = null;
    this.compressionLevel = options.compressionLevel || 9;
    this.qualityLevel = options.qualityLevel || 1;
    this.scalingMode = options.scalingMode || "off";
  }

  // Initialize the display manager with container and session
  init(containerId, session) {
    this.container = document.getElementById(containerId);
    this.session = session;

    if (!this.container) {
      console.error(`Container with ID ${containerId} not found`);
      return false;
    }

    console.log(`Initialized display manager for session: ${session?.id || "unknown"}`);
    return true;
  }

  // Connect to the VNC server
  connect() {
    if (!this.container) {
      message.error("Container not initialized");
      return false;
    }

    const url = "wss://" + this.url.replace("http://", "") + "/websockify";
    try {
      this.rfb = new RFB(this.container, url, {
        wsProtocols: ["binary"],
        credentials: this.credentials
      });

      // Set up event listeners
      this.rfb.addEventListener("connect", this._handleConnect.bind(this));
      this.rfb.addEventListener("disconnect", this._handleDisconnect.bind(this));
      this.rfb.addEventListener("credentialsrequired", this._handleCredentialsRequired.bind(this));
      this.rfb.addEventListener("desktopname", this._handleDesktopName.bind(this));
      this.rfb.addEventListener("clipboard", this._handleClipboardReceive.bind(this));

      // Set parameters
      this.rfb.viewOnly = this.viewOnly;
      this.rfb.compressionLevel = this.compressionLevel;
      this.rfb.qualityLevel = this.qualityLevel;

      this.applyScalingMode();

      return true;
    } catch (error) {
      console.error("Failed to connect to server:", error);
      message.error("Failed to connect to VNC server");
      return false;
    }
  }

  // Apply the current scaling mode settings to the RFB connection
  applyScalingMode() {
    if (!this.rfb) return;

    switch (this.scalingMode) {
      case "scale":
        this.rfb.scaleViewport = true;
        this.rfb.resizeSession = false;
        this.scaleViewport = true;
        this.resizeSession = false;
        break;
      case "remote":
        this.rfb.scaleViewport = false;
        this.rfb.resizeSession = true;
        this.scaleViewport = false;
        this.resizeSession = true;
        break;
      case "off":
      default:
        this.rfb.scaleViewport = false;
        this.rfb.resizeSession = false;
        this.scaleViewport = false;
        this.resizeSession = false;
        break;
    }

    // Update the clipViewport status based on scaling mode
    // When scaling is enabled, we typically don't want to clip
    this.rfb.clipViewport = this.clipViewport && this.scalingMode !== "scale";
  }

  // Set the scaling mode
  setScalingMode(mode) {
    if (!["off", "scale", "remote"].includes(mode)) {
      console.error(`Invalid scaling mode: ${mode}`);
      return false;
    }

    this.scalingMode = mode;

    if (this.rfb && this.connected) {
      this.applyScalingMode();
      return true;
    }

    return false;
  }

  // Get the current scaling mode
  getScalingMode() {
    return this.scalingMode;
  }

  // Disconnect from the VNC server
  disconnect() {
    if (this.rfb) {
      this.rfb.disconnect();
      this.rfb = null;
      this.connected = false;
    }
  }

  // Handle connection event
  _handleConnect() {
    this.connected = true;
    console.log("Connected to VNC server for session:", this.session?.id || "unknown");
    this.onConnect();
  }

  // Handle disconnection event
  _handleDisconnect(e) {
    this.connected = false;
    console.log("Disconnected from VNC server", e.detail.clean ? "(clean)" : "(dirty)");
    console.log("Disconnected from session:", this.session?.id || "unknown");
    this.onDisconnect(e.detail.clean);
  }

  // Handle credentials required event
  _handleCredentialsRequired(e) {
    console.log("Credentials required", e);
    this.onCredentialsRequired(e);
  }

  // Handle desktop name event
  _handleDesktopName(e) {
    console.log("Desktop name:", e.detail.name);
    this.onDesktopName(e.detail.name);
  }

  // Handle clipboard receive event
  _handleClipboardReceive(e) {
    this.clipboardText = e.detail.text;
    console.log("Clipboard received:", this.clipboardText);
  }

  // Send clipboard text to the VNC server
  sendClipboardText(text) {
    if (this.rfb && this.connected) {
      console.log({ text });
      this.rfb.clipboardPasteFrom(text);
      return true;
    }
    return false;
  }

  // Toggle full screen mode
  toggleFullScreen() {
    const elem = document.documentElement;

    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      }
      return true;
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
      return false;
    }
  }

  // Toggle audio
  toggleAudio() {
    this.audioEnabled = !this.audioEnabled;
    console.log("Audio toggled:", this.audioEnabled);
    return this.audioEnabled;
  }

  // Toggle microphone
  toggleMicrophone() {
    this.recordingEnabled = !this.recordingEnabled;
    console.log("Microphone toggled:", this.recordingEnabled);
    return this.recordingEnabled;
  }

  // Handle file upload
  handleFileUpload() {
    // Implement file upload logic
    message.info("File upload functionality not implemented yet");
  }

  // Send special key combinations
  sendCtrlAltDel() {
    if (this.rfb && this.connected) {
      this.rfb.sendCtrlAltDel();
      return true;
    }
    return false;
  }

  // Set stream quality parameters
  setQuality(quality) {
    if (this.rfb && this.connected) {
      this.rfb.qualityLevel = quality;
      this.qualityLevel = quality;
      console.log("setQuality", quality);
      return true;
    }
    return false;
  }

  // Set compression level
  setCompression(level) {
    if (this.rfb && this.connected) {
      this.rfb.compressionLevel = level;
      this.compressionLevel = level;
      console.log("Compression level set to:", level);
      return true;
    }
    return false;
  }

  // Get current compression level
  getCompressionLevel() {
    if (this.rfb && this.connected) {
      return this.rfb.compressionLevel;
    }
    return this.compressionLevel;
  }

  // Get current quality level
  getQuality() {
    if (this.rfb && this.connected) {
      return this.rfb.qualityLevel;
    }
    return this.qualityLevel;
  }

  // Handle scaling mode settings
  handleScalingSettings() {
    return {
      currentMode: this.getScalingMode(),
      setScalingMode: this.setScalingMode.bind(this)
    };
  }

  handleCompressionSettings() {
    return {
      currentLevel: this.getCompressionLevel(),
      setCompression: this.setCompression.bind(this)
    };
  }

  getStatus() {
    return {
      connected: this.connected,
      audioEnabled: this.audioEnabled,
      recordingEnabled: this.recordingEnabled,
      viewOnly: this.rfb ? this.rfb.viewOnly : this.viewOnly,
      scaleViewport: this.rfb ? this.rfb.scaleViewport : this.scaleViewport,
      compressionLevel: this.getCompressionLevel(),
      scalingMode: this.getScalingMode()
    };
  }

  getVncActions() {
    return {
      onPaste: (text) => this.sendClipboardText(text),
      onFullScreen: () => this.toggleFullScreen(),
      onUploadFile: () => this.handleFileUpload(),
      handleToggleAudio: () => this.toggleAudio(),
      handleToggleMicro: () => this.toggleMicrophone(),
      sendCtrlAltDel: () => this.sendCtrlAltDel(),
      setQuality: (quality) => this.setQuality(quality),
      setCompression: (level) => this.setCompression(level),
      getCompressionLevel: () => this.getCompressionLevel(),
      getQuality: () => this.getQuality(),
      setScalingMode: (mode) => this.setScalingMode(mode),
      getScalingMode: () => this.getScalingMode(),
      handleCompressionSettings: () => this.handleCompressionSettings(),
      handleScalingSettings: () => this.handleScalingSettings()
    };
  }
}

export default ProxyDisplayManager;
